import { useEffect, useMemo, useState } from "react";

import { useCaseDetailData } from "@/components/case-checking/CaseDetailDataContext";
import {
  listProblemStatementRuns,
  type ProblemStatementHumanReview,
  type ProblemStatementNarrative,
  type ProblemStatementRun,
} from "@/services/api/case-checking";

function useStandaloneProblemStatementRun(caseId: string, enabled: boolean) {
  const [run, setRun] = useState<ProblemStatementRun | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    listProblemStatementRuns(caseId, 1)
      .then((runs) => {
        if (cancelled) return;
        setRun(runs[0] ?? null);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load outcomes");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [caseId, enabled]);

  return { run, setRun, loading, error, setError };
}

export function useProblemStatementRun(caseId: string) {
  const shared = useCaseDetailData();
  const useShared = Boolean(shared && shared.caseId === caseId);
  const standalone = useStandaloneProblemStatementRun(caseId, !useShared);

  const detailData = useShared && shared ? shared : null;
  const run = detailData ? detailData.problemStatementRun : standalone.run;
  const setRun = detailData ? detailData.setProblemStatementRun : standalone.setRun;
  const loading = detailData ? detailData.psRunLoading : standalone.loading;
  const error = detailData ? detailData.psRunError : standalone.error;
  const setError = standalone.setError;

  const narrativesById = useMemo(() => {
    const map = new Map<string, ProblemStatementNarrative>();
    for (const n of run?.narratives ?? []) map.set(n.problem_statement_id, n);
    return map;
  }, [run?.narratives]);

  const humanReviewsById = useMemo(() => {
    const map = new Map<string, ProblemStatementHumanReview>();
    for (const r of run?.human_reviews ?? []) map.set(r.problem_statement_id, r);
    return map;
  }, [run?.human_reviews]);

  const scorable = useMemo(
    () => (run?.rollups ?? []).filter((r) => r.rollup_kind !== "final"),
    [run?.rollups],
  );

  return {
    run,
    setRun,
    loading,
    error,
    setError,
    scorable,
    narrativesById,
    humanReviewsById,
  };
}
